import { Check, Lock } from "lucide-react";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";
import type { VanKhiKind, VanKhiPeriod } from "@/lib/boide-api";

/**
 * Thanh chọn kỳ.
 * - `day`: dải ngày (3 ngày trước → hôm nay → 6 ngày tới), số dương + âm bên dưới.
 * - `week`/`month`: chip các kỳ trước / hiện tại / kỳ sau.
 * Kỳ hiện tại miễn phí; kỳ khác có ổ khoá = mở bằng số dư (vẫn bấm chọn được).
 */
export function VanKhiPeriodStrip({
  kind,
  periods,
  selectedKey,
  onSelect,
}: {
  kind: VanKhiKind;
  periods: VanKhiPeriod[];
  selectedKey: string | null;
  onSelect: (period: VanKhiPeriod) => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = stripRef.current;
    const selected = strip?.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
    if (!strip || !selected) return;
    // Scroll only the rail; scrollIntoView also moves the page past its heading.
    strip.scrollLeft = selected.offsetLeft - (strip.clientWidth - selected.clientWidth) / 2;
  }, [selectedKey, kind]);

  if (kind === "day") {
    return (
      <section aria-label="Chọn ngày muốn xem">
        <div className="mb-2 flex items-center justify-between gap-3 px-1">
          <p className="text-xs font-semibold text-foreground">Chọn ngày</p>
          <p className="text-[10px] text-muted-foreground">
            <Check className="mr-1 inline size-3 text-element-wood" />
            Đã mở
            <Lock className="ml-2 mr-1 inline size-3" />
            Trả phí
          </p>
        </div>
        <div ref={stripRef} className="almanac-period-rail almanac-period-rail--days">
          {periods.map((period) => (
            <DayCell
              key={period.key}
              period={period}
              active={period.key === selectedKey}
              onSelect={onSelect}
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Chọn kỳ muốn xem">
      <p className="mb-2 px-1 text-xs font-semibold text-foreground">
        Chọn {kind === "week" ? "tuần" : "tháng"}
      </p>
      <div ref={stripRef} className="almanac-period-rail">
        {periods.map((period) => (
          <PeriodChip
            key={period.key}
            period={period}
            active={period.key === selectedKey}
            onSelect={onSelect}
          />
        ))}
      </div>
    </section>
  );
}

/** Mọi kỳ đều chọn được: kỳ hiện tại miễn phí, kỳ khác hiện màn mở bằng số dư. */
function isSelectable(_period: VanKhiPeriod): boolean {
  return true;
}

/** Kỳ phải trả phí (chưa mở, không phải kỳ hiện tại) — gắn ổ khoá. */
function isPaid(period: VanKhiPeriod): boolean {
  return period.state !== "open" && !period.has_reading;
}

function DayCell({
  period,
  active,
  onSelect,
}: {
  period: VanKhiPeriod;
  active: boolean;
  onSelect: (period: VanKhiPeriod) => void;
}) {
  const selectable = isSelectable(period);
  const date = new Date(`${period.start_date}T12:00:00Z`);
  const dayNumber = date.getUTCDate();
  const weekday = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"][date.getUTCDay()];

  return (
    <button
      type="button"
      disabled={!selectable}
      onClick={() => selectable && onSelect(period)}
      aria-current={active ? "date" : undefined}
      aria-pressed={active}
      aria-label={`${period.label}, ${period.sub_label}${period.state === "open" ? ", hiện tại, miễn phí" : ""}`}
      className="almanac-day"
    >
      <span
        className={cn(
          "text-[10px] tracking-wide",
          active ? "text-primary-foreground/75" : "text-muted-foreground",
        )}
      >
        {weekday}
      </span>
      <span
        className={cn(
          "almanac-day-number",
          active ? "text-primary-foreground" : "text-foreground/85",
        )}
      >
        {dayNumber}
      </span>
      <span
        className={cn(
          "text-[9px]",
          active ? "text-primary-foreground/75" : "text-muted-foreground",
        )}
      >
        {period.sub_label.replace(" ÂL", "")}
      </span>
      {period.has_reading ? (
        <Check
          className={cn("size-3", active ? "text-primary-foreground" : "text-element-wood")}
          aria-label="Đã mở"
        />
      ) : isPaid(period) ? (
        <Lock
          className={cn("size-3", active ? "text-primary-foreground/75" : "text-muted-foreground")}
          aria-label="Mở bằng số dư"
        />
      ) : (
        <span className="size-3" />
      )}
    </button>
  );
}

function PeriodChip({
  period,
  active,
  onSelect,
}: {
  period: VanKhiPeriod;
  active: boolean;
  onSelect: (period: VanKhiPeriod) => void;
}) {
  const selectable = isSelectable(period);
  return (
    <button
      type="button"
      disabled={!selectable}
      onClick={() => selectable && onSelect(period)}
      aria-pressed={active}
      className="almanac-period-chip"
    >
      <span className="flex items-center gap-1.5">
        <span
          className={cn(
            "font-display text-sm font-medium",
            active ? "text-primary" : "text-foreground/85",
          )}
        >
          {period.label}
        </span>
        {period.has_reading ? <Check className="size-3.5 text-element-wood" /> : null}
        {isPaid(period) ? <Lock className="size-3 text-muted-foreground" /> : null}
      </span>
      <span className="text-[11px] text-muted-foreground">
        {period.state === "open" ? "Hiện tại · miễn phí" : period.sub_label}
      </span>
    </button>
  );
}
